/**
 * Analytics Service
 * 
 * Comprehensive analytics service for aggregating and calculating
 * business metrics including stores, notes, orders, and revenue.
 * 
 * @module AnalyticsService
 */

import {
  collection,
  collectionGroup,
  query,
  where,
  getDocs,
  Timestamp,
  orderBy,
  limit
} from 'firebase/firestore';
import type { QueryDocumentSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getLocalCalendarYMD } from '../utils/calendarDate';

/**
 * Analytics period types
 */
export type AnalyticsPeriod = 'today' | 'week' | 'month' | 'year' | 'custom';

/**
 * Analytics metrics interface
 */
export interface AnalyticsMetrics {
  // Store metrics
  totalStores: number;
  storesWithNotes: number;
  storesWithOrders: number;
  storesVisited: number;
  
  // Notes metrics
  totalNotes: number;
  notesThisPeriod: number;
  averageNotesPerStore: number;
  
  // Order metrics
  totalOrders: number;
  ordersThisPeriod: number;
  pendingOrders: number;
  completedOrders: number;
  averageOrdersPerStore: number;
  
  // Revenue metrics
  totalRevenue: number;
  revenueThisPeriod: number;
  averageOrderValue: number;
  averageRevenuePerStore: number;
  
  // Period comparison
  periodComparison?: {
    ordersGrowth: number;
    revenueGrowth: number;
    notesGrowth: number;
    storesGrowth: number;
  };
  
  // Period info
  period: AnalyticsPeriod;
  startDate: Date;
  endDate: Date;
}

/**
 * Daily bucket for time-series charts (orders, revenue, notes, visits, views by date).
 */
export interface TimeSeriesBucket {
  date: string;
  orders: number;
  revenue: number;
  notes: number;
  visits: number;
  views: number;
}

/**
 * Analytics Service Class
 */
export class AnalyticsService {
  private static readonly STORES_COLLECTION = 'Stores';
  private static readonly ORDERS_COLLECTION = 'StoreOrders';
  private static readonly NOTES_COLLECTION = 'notes';
  private static readonly ACTIVITIES_COLLECTION = 'activities';

  /**
   * Get comprehensive analytics metrics
   * 
   * @param period - Analytics period
   * @param startDate - Optional start date for custom period
   * @param endDate - Optional end date for custom period
   * @returns Promise resolving to analytics metrics
   */
  static async getAnalyticsMetrics(
    period: AnalyticsPeriod = 'month',
    startDate?: Date,
    endDate?: Date
  ): Promise<AnalyticsMetrics> {
    try {
      const dates = this.getPeriodDates(period, startDate, endDate);
      const previousDates = this.getPreviousPeriodDates(period, dates.start, dates.end);

      // Get all stores (single query, typically small)
      const storesSnapshot = await getDocs(collection(db, this.STORES_COLLECTION));
      const totalStores = storesSnapshot.size;

      // Fetch only orders in current and previous period (period-scoped queries).
      // orderDate is stored as YYYY-MM-DD string in Firestore.
      const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
      const [currentQ, previousQ] = [
        query(
          ordersQuery,
          where('orderDate', '>=', getLocalCalendarYMD(dates.start)),
          where('orderDate', '<=', getLocalCalendarYMD(dates.end))
        ),
        query(
          ordersQuery,
          where('orderDate', '>=', getLocalCalendarYMD(previousDates.start)),
          where('orderDate', '<=', getLocalCalendarYMD(previousDates.end))
        )
      ];

      const [currentSnapshot, previousSnapshot] = await Promise.all([
        getDocs(currentQ),
        getDocs(previousQ)
      ]);

      const mapOrder = (doc: QueryDocumentSnapshot) => {
        const data = doc.data();
        const storeId = doc.ref.parent.parent?.id || '';
        const stored = data.totalAmount ?? data.TotalAmount ?? data.total ?? data.Total ?? data.grandTotal ?? data.GrandTotal;
        let totalAmount = typeof stored === 'number' ? stored : 0;
        if (totalAmount === 0) {
          const items = [
            ...(data.heritageItems || []),
            ...(data.livingstonItems || []),
            ...(data.flowerItems || []),
            ...(data.vegetableItems || [])
          ];
          totalAmount = items.reduce(
            (sum, item) => sum + ((item.price || 0) * (item.quantity || 0)),
            0
          );
        }
        return {
          id: doc.id,
          storeId,
          orderDate: data.orderDate || data.OrderDate || data.createdAt || data.CreatedAt || data.timestamp || data.Timestamp,
          totalAmount,
          status: data.status || data.Status || 'unknown'
        };
      };

      const ordersThisPeriod = currentSnapshot.docs.map(mapOrder);
      const previousPeriodOrders = previousSnapshot.docs.map(mapOrder);
      const totalOrders = ordersThisPeriod.length;
      const ordersThisPeriodCount = ordersThisPeriod.length;
      const pendingOrders = ordersThisPeriod.filter(o =>
        o.status === 'pending' || o.status === 'submitted' || o.status === 'draft'
      ).length;
      const completedOrders = ordersThisPeriod.filter(o =>
        o.status === 'completed' || o.status === 'approved'
      ).length;

      const revenueThisPeriod = ordersThisPeriod.reduce((sum, order) => {
        const amount = typeof order.totalAmount === 'number' ? order.totalAmount : 0;
        return sum + amount;
      }, 0);
      const previousPeriodRevenue = previousPeriodOrders.reduce((sum, order) => {
        const amount = typeof order.totalAmount === 'number' ? order.totalAmount : 0;
        return sum + amount;
      }, 0);
      const totalRevenue = revenueThisPeriod;
      const averageOrderValue = ordersThisPeriodCount > 0 ? revenueThisPeriod / ordersThisPeriodCount : 0;

      const storesWithOrdersSet = new Set<string>();
      ordersThisPeriod.forEach(order => {
        if (order.storeId) storesWithOrdersSet.add(order.storeId);
      });
      const storesWithOrders = storesWithOrdersSet.size;

      // Notes: use collectionGroup with date filter instead of per-store getDocs
      let totalNotes = 0;
      let notesThisPeriodCount = 0;
      const storesWithNotesSet = new Set<string>();
      const notesGroup = collectionGroup(db, this.NOTES_COLLECTION);

      try {
        const notesQ = query(
          notesGroup,
          where('createdAt', '>=', Timestamp.fromDate(dates.start)),
          where('createdAt', '<=', Timestamp.fromDate(dates.end))
        );
        const notesSnapshot = await getDocs(notesQ);
        notesThisPeriodCount = notesSnapshot.size;
        notesSnapshot.docs.forEach(noteDoc => {
          const storeId = noteDoc.ref.parent.parent?.id || '';
          if (storeId) storesWithNotesSet.add(storeId);
        });
        totalNotes = notesThisPeriodCount;
      } catch (notesErr: any) {
        console.warn('AnalyticsService: collectionGroup notes failed, skipping notes metrics:', notesErr?.message);
      }

      const storesWithNotes = storesWithNotesSet.size;
      // Stores visited: count stores with at least one note in the period (aligns with notes data)
      const storesVisited = storesWithNotes;

      // Fetch previous period notes and stores for growth calculations
      let previousPeriodNotesCount = 0;
      let previousPeriodStoresWithNotes = 0;
      try {
        const prevNotesQ = query(
          notesGroup,
          where('createdAt', '>=', Timestamp.fromDate(previousDates.start)),
          where('createdAt', '<=', Timestamp.fromDate(previousDates.end))
        );
        const prevNotesSnapshot = await getDocs(prevNotesQ);
        previousPeriodNotesCount = prevNotesSnapshot.size;
        const prevStoresWithNotesSet = new Set<string>();
        prevNotesSnapshot.docs.forEach(noteDoc => {
          const storeId = noteDoc.ref.parent.parent?.id || '';
          if (storeId) prevStoresWithNotesSet.add(storeId);
        });
        previousPeriodStoresWithNotes = prevStoresWithNotesSet.size;
      } catch (prevNotesErr: any) {
        console.warn('AnalyticsService: previous period notes query failed:', prevNotesErr?.message);
      }
      
      // Calculate growth percentages
      const previousPeriodOrdersCount = previousPeriodOrders.length;
      const ordersGrowth = previousPeriodOrdersCount > 0
        ? ((ordersThisPeriodCount - previousPeriodOrdersCount) / previousPeriodOrdersCount) * 100
        : ordersThisPeriodCount > 0 ? 100 : 0;
      
      const revenueGrowth = previousPeriodRevenue > 0
        ? ((revenueThisPeriod - previousPeriodRevenue) / previousPeriodRevenue) * 100
        : revenueThisPeriod > 0 ? 100 : 0;
      
      const notesGrowth = previousPeriodNotesCount > 0
        ? ((notesThisPeriodCount - previousPeriodNotesCount) / previousPeriodNotesCount) * 100
        : notesThisPeriodCount > 0 ? 100 : 0;
      
      const storesGrowth = previousPeriodStoresWithNotes > 0
        ? ((storesVisited - previousPeriodStoresWithNotes) / previousPeriodStoresWithNotes) * 100
        : storesVisited > 0 ? 100 : 0;
      
      const result = {
        totalStores,
        storesWithNotes,
        storesWithOrders,
        storesVisited,
        totalNotes,
        notesThisPeriod: notesThisPeriodCount,
        averageNotesPerStore: totalStores > 0 ? totalNotes / totalStores : 0,
        totalOrders,
        ordersThisPeriod: ordersThisPeriodCount,
        pendingOrders,
        completedOrders,
        averageOrdersPerStore: totalStores > 0 ? ordersThisPeriodCount / totalStores : 0,
        totalRevenue,
        revenueThisPeriod,
        averageOrderValue,
        averageRevenuePerStore: totalStores > 0 ? revenueThisPeriod / totalStores : 0,
        periodComparison: {
          ordersGrowth,
          revenueGrowth,
          notesGrowth,
          storesGrowth
        },
        period,
        startDate: dates.start,
        endDate: dates.end
      };
      
      console.log('AnalyticsService: Final metrics result:', {
        totalStores: result.totalStores,
        totalOrders: result.totalOrders,
        ordersThisPeriod: result.ordersThisPeriod,
        totalRevenue: result.totalRevenue,
        revenueThisPeriod: result.revenueThisPeriod,
        totalNotes: result.totalNotes,
        notesThisPeriod: result.notesThisPeriod
      });
      
      return result;
    } catch (error: any) {
      console.error('Error getting analytics metrics:', error);
      throw new Error(`Failed to get analytics metrics: ${error.message}`);
    }
  }

  /**
   * Get time-series data for charts: orders, revenue, notes, visits, and views by day.
   * Fetches from Firestore and aggregates into daily buckets for the given date range.
   *
   * @param start - Start date (inclusive)
   * @param end - End date (inclusive)
   * @returns Promise resolving to array of daily buckets
   */
  static async getTimeSeriesData(start: Date, end: Date): Promise<TimeSeriesBucket[]> {
    const buckets: Record<string, TimeSeriesBucket> = {};
    const cur = new Date(start);
    cur.setHours(0, 0, 0, 0);
    const endVal = new Date(end);
    endVal.setHours(23, 59, 59, 999);
    while (cur <= endVal) {
      const d = getLocalCalendarYMD(cur);
      buckets[d] = { date: d, orders: 0, revenue: 0, notes: 0, visits: 0, views: 0 };
      cur.setDate(cur.getDate() + 1);
    }

    const ordersQuery = collectionGroup(db, this.ORDERS_COLLECTION);
    const ordersQ = query(
      ordersQuery,
      where('orderDate', '>=', getLocalCalendarYMD(start)),
      where('orderDate', '<=', getLocalCalendarYMD(end))
    );
    const mapOrder = (doc: QueryDocumentSnapshot) => {
      const data = doc.data();
      const storeId = doc.ref.parent.parent?.id || '';
      let totalAmount = data.totalAmount ?? data.TotalAmount ?? data.total ?? data.Total ?? data.grandTotal ?? data.GrandTotal;
      if (typeof totalAmount !== 'number') totalAmount = 0;
      if (totalAmount === 0) {
        const items = [
          ...(data.heritageItems || []),
          ...(data.livingstonItems || []),
          ...(data.flowerItems || []),
          ...(data.vegetableItems || [])
        ];
        totalAmount = items.reduce((s, i) => s + ((i.price || 0) * (i.quantity || 0)), 0);
      }
      const orderDate = data.orderDate || data.OrderDate;
      const ymd =
        typeof orderDate === 'string'
          ? orderDate.trim()
          : orderDate && typeof orderDate === 'object' && 'toDate' in orderDate
            ? getLocalCalendarYMD((orderDate as Timestamp).toDate())
            : getLocalCalendarYMD(new Date());
      return { storeId, totalAmount, orderDate: ymd };
    };

    let notesByDay: Record<string, { count: number; storeIds: Set<string> }> = {};
    const notesGroup = collectionGroup(db, this.NOTES_COLLECTION);
    const notesQ = query(
      notesGroup,
      where('createdAt', '>=', Timestamp.fromDate(start)),
      where('createdAt', '<=', Timestamp.fromDate(endVal))
    );

    const activitiesRef = collection(db, this.ACTIVITIES_COLLECTION);
    const activitiesQ = query(
      activitiesRef,
      where('timestamp', '>=', Timestamp.fromDate(start)),
      where('timestamp', '<=', Timestamp.fromDate(endVal)),
      orderBy('timestamp', 'asc'),
      limit(5000)
    );

    const [ordersSnap, notesSnap, activitiesSnap] = await Promise.all([
      getDocs(ordersQ),
      getDocs(notesQ).catch(() => null),
      getDocs(activitiesQ).catch(() => null)
    ]);

    ordersSnap.docs.forEach((doc) => {
      const m = mapOrder(doc);
      if (buckets[m.orderDate]) {
        buckets[m.orderDate].orders += 1;
        buckets[m.orderDate].revenue += m.totalAmount;
      }
    });

    if (notesSnap) {
      notesSnap.docs.forEach((noteDoc) => {
        const data = noteDoc.data();
        const storeId = noteDoc.ref.parent.parent?.id ?? '';
        const created = data.createdAt?.toDate?.() ?? data.timestamp?.toDate?.();
        const ymd = created ? getLocalCalendarYMD(created) : getLocalCalendarYMD(new Date());
        if (!notesByDay[ymd]) notesByDay[ymd] = { count: 0, storeIds: new Set() };
        notesByDay[ymd].count += 1;
        if (storeId) notesByDay[ymd].storeIds.add(storeId);
      });
      Object.entries(notesByDay).forEach(([ymd, v]) => {
        if (buckets[ymd]) {
          buckets[ymd].notes = v.count;
          buckets[ymd].visits = v.storeIds.size;
        }
      });
    }

    if (activitiesSnap) {
      activitiesSnap.docs.forEach((doc) => {
        const data = doc.data();
        const ts = data.timestamp?.toDate?.();
        const ymd = ts ? getLocalCalendarYMD(ts) : getLocalCalendarYMD(new Date());
        if (buckets[ymd]) buckets[ymd].views += 1;
      });
    }

    return Object.keys(buckets)
      .sort()
      .map((d) => buckets[d]);
  }

  /**
   * Get period dates
   *
   * @private
   */
  private static getPeriodDates(
    period: AnalyticsPeriod,
    customStart?: Date,
    customEnd?: Date
  ): { start: Date; end: Date } {
    const now = new Date();
    let start: Date;
    let end: Date = now;

    switch (period) {
      case 'today':
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        break;
      case 'week':
        start = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
        break;
      case 'month':
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        break;
      case 'year':
        start = new Date(now.getFullYear(), 0, 1);
        break;
      case 'custom':
        start = customStart || new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
        end = customEnd || now;
        break;
      default:
        start = new Date(now.getFullYear(), now.getMonth(), 1);
    }

    return { start, end };
  }

  /**
   * Get previous period dates for comparison
   * 
   * @private
   */
  private static getPreviousPeriodDates(
    period: AnalyticsPeriod,
    currentStart: Date,
    currentEnd: Date
  ): { start: Date; end: Date } {
    const duration = currentEnd.getTime() - currentStart.getTime();
    
    return {
      start: new Date(currentStart.getTime() - duration),
      end: new Date(currentStart.getTime() - 1)
    };
  }
}

